const inrFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function paiseToRupees(paise: number): number {
  return paise / 100;
}

export function rupeesToPaise(rupees: number): number {
  if (!Number.isFinite(rupees)) {
    throw new Error('rupees must be a finite number');
  }
  // Round via toFixed to dodge IEEE-754 representation drift
  // (e.g., 1.005 * 100 === 100.49999... which would round down).
  const fixed = Number((rupees * 100).toFixed(8));
  return Math.round(fixed);
}

export function formatInr(paise: number): string {
  return inrFormatter.format(paiseToRupees(paise));
}

// PDF-specific currency formatter. @react-pdf/renderer uses Helvetica by
// default, which does not include the ₹ glyph (U+20B9) — Helvetica was
// finalized before the symbol was assigned in 2010. The renderer's missing-
// glyph fallback substitutes a thin shape that reads as a superscript "¹"
// in the rendered PDF, leading to "¹10,300.00" instead of "₹10,300.00".
//
// Two ways to fix this: register a TTF that includes ₹ (adds ~50KB to the
// PDF and slows render), or sidestep the glyph entirely. We sidestep with
// the conventional Indian fallback "Rs." which every PDF viewer can render
// from the base 14 fonts. The browser path still uses formatInr above and
// gets the proper rupee symbol from the system font stack.
const inrPdfFormatter = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatInrForPdf(paise: number): string {
  // NBSP (U+00A0) between "Rs." and the digits keeps the prefix from
  // word-wrapping away from the amount in narrow PDF columns.
  return `Rs.\u00A0${inrPdfFormatter.format(paiseToRupees(paise))}`;
}

// Spell out an amount in Indian numbering (lakh / crore) — every GST
// invoice in India shows the words form alongside digits, both for legal
// clarity and to disambiguate transcription errors. We match how a jeweler
// reads out a bill aloud ("ten thousand three hundred rupees and fifty
// paise only"), not the Western thousands grouping.
const ONES = [
  'Zero',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
  'Sixteen',
  'Seventeen',
  'Eighteen',
  'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function twoDigit(n: number): string {
  if (n < 20) return ONES[n] ?? '';
  const t = Math.floor(n / 10);
  const o = n % 10;
  return o === 0 ? (TENS[t] ?? '') : `${TENS[t]} ${ONES[o]}`;
}

function threeDigit(n: number): string {
  if (n === 0) return '';
  if (n < 100) return twoDigit(n);
  const h = Math.floor(n / 100);
  const rest = n % 100;
  return rest === 0 ? `${ONES[h]} Hundred` : `${ONES[h]} Hundred ${twoDigit(rest)}`;
}

function rupeesToWords(rupees: number): string {
  if (rupees === 0) return 'Zero';
  if (rupees < 0 || !Number.isFinite(rupees)) {
    throw new Error(`rupeesToWords: out of range: ${rupees}`);
  }
  const crore = Math.floor(rupees / 10_000_000);
  const lakh = Math.floor((rupees % 10_000_000) / 100_000);
  const thousand = Math.floor((rupees % 100_000) / 1_000);
  const hundredsAndBelow = rupees % 1_000;
  const parts: string[] = [];
  if (crore > 0) parts.push(`${twoDigit(crore)} Crore`);
  if (lakh > 0) parts.push(`${twoDigit(lakh)} Lakh`);
  if (thousand > 0) parts.push(`${twoDigit(thousand)} Thousand`);
  if (hundredsAndBelow > 0) parts.push(threeDigit(hundredsAndBelow));
  return parts.join(' ');
}

export function formatInrInWords(paise: number): string {
  if (!Number.isFinite(paise) || paise < 0) {
    throw new Error(`formatInrInWords: invalid paise: ${paise}`);
  }
  const rounded = Math.round(paise);
  const rupees = Math.floor(rounded / 100);
  const remainderPaise = rounded % 100;
  const rupeesPart = rupees === 0 ? 'Zero' : rupeesToWords(rupees);
  if (remainderPaise === 0) return `${rupeesPart} Rupees Only`;
  return `${rupeesPart} Rupees and ${twoDigit(remainderPaise)} Paise Only`;
}
